// Locker (§7.3, §14.2): your bean on a turntable (the renderer's locker stage, drag to spin) with
// tabs for Color, Pattern, Hat, Eyes, Honk, Trail, Scream, Celebration, Identity preference and
// the daily Shop. Every item is a card; selecting one previews it on the turntable (honks and
// screams also play), the side panel says what it is and how to get it (EQUIP, "Unlocks at Level
// 12", BUY 300 with a confirm step), locked cards show their level or price. LB/RB or Q/E switch
// tabs; arrows / D-pad / touch pick cards; Esc / B closes and the turntable shows your real look.
import { CATALOG, HAT_NONE, IDENTITIES, NO_IDENTITY_PREF, type CatalogItem, type Cosmetics, type CosmeticCategory } from '@shared/cosmetics';
import type { ItemStatus } from '../../profile/ProfileService';
import type { LiveScreen, NavAction } from '../contracts';
import type { UiDeps } from '../deps';
import { beanIcon, CATEGORY_ICONS, icon, identityBadge } from '../icons';
import { el, replayClass, setHidden, setText, toggleClass } from '../ui';
import { button, closeButton, setLabel, tabs } from '../widgets';
import { confetti } from '../anim';

type TabId = CosmeticCategory | 'identity' | 'shop';

const TAB_LABELS: Readonly<Record<TabId, string>> = {
  color: 'Color', pattern: 'Pattern', hat: 'Hat', eyes: 'Eyes', honk: 'Honk', trail: 'Trail', scream: 'Scream',
  celebration: 'Party', identity: 'Identity', shop: 'Shop',
};
const TAB_ORDER: readonly TabId[] = ['color', 'pattern', 'hat', 'eyes', 'honk', 'trail', 'scream', 'celebration', 'identity', 'shop'];
/** Categories whose cards show a rendered bean wearing the item. */
const PORTRAIT_CATS: ReadonlySet<CosmeticCategory> = new Set<CosmeticCategory>(['pattern', 'hat', 'eyes']);
const CARD_PORTRAIT = 96;
const TRAIL_ICONS: Readonly<Record<string, string>> = { none: 'x', sparkles: 'sparkle', bubbles: 'sweat', hearts: 'heart', musicNotes: 'music', rainbow: 'trail' };
const CELEBRATION_ICONS: Readonly<Record<string, string>> = { confettiCannon: 'party', fireworks: 'sparkle', flowerBurst: 'heart', discoBall: 'star' };

interface Card { node: HTMLButtonElement; cat: CosmeticCategory | 'identityPref'; id: number; art: HTMLElement; lazy: boolean }

/** One visual "slot" in the grid: a catalog item, hat none, or an identity. */
interface Entry { cat: CosmeticCategory | 'identityPref'; id: number; name: string; item: CatalogItem | null }

function entriesFor(tab: TabId): Entry[] {
  if (tab === 'identity') {
    return [{ cat: 'identityPref', id: NO_IDENTITY_PREF, name: 'No preference', item: null },
      ...IDENTITIES.map((idn, i) => ({ cat: 'identityPref' as const, id: i, name: idn.name, item: null }))];
  }
  if (tab === 'shop') return [];
  const list: Entry[] = CATALOG.filter((it) => it.cat === tab).map((it) => ({ cat: tab, id: it.id, name: it.name, item: it }));
  if (tab === 'hat') list.unshift({ cat: 'hat', id: HAT_NONE, name: 'No hat', item: null });
  return list;
}

/** Seconds until the shop rotates (UTC midnight). */
function shopSecondsLeft(): number {
  const now = new Date();
  const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  return Math.max(0, Math.round((next - now.getTime()) / 1000));
}

export function create(deps: UiDeps): LiveScreen {
  const profiles = deps.services.profiles;
  let tab: TabId = 'color';
  let selected: Entry | null = null;
  let preview: Cosmetics = { ...deps.profile().cosmetics };
  let confirmBuy = false;
  let cards: Card[] = [];
  let lazyQueue: Card[] = [];

  const bucks = el('span', { class: 'lk-bucks-num' });
  const bucksChip = el('span', { class: 'chip yellow lk-bucks' }, icon('coin'), bucks);
  const grid = el('div', { class: 'lk-grid', attrs: { role: 'listbox', 'aria-label': 'Items' } });
  const shopNote = el('div', { class: 'lk-shop-note' });
  const tabStrip = tabs('Locker', TAB_ORDER.map((t) => ({ id: t, label: TAB_LABELS[t], icon: CATEGORY_ICONS[t] })), (id) => selectTab(id as TabId));
  tabStrip.el.setAttribute('data-tabs', '');

  // Detail panel.
  const dName = el('div', { class: 'lk-d-name' });
  const dCat = el('div', { class: 'lk-d-cat' });
  const dStatus = el('div', { class: 'lk-d-status' });
  const equip = button({ label: 'EQUIP', icon: 'check', variant: 'pink', cls: 'wide', onClick: () => doEquip() });
  const buy = button({ label: 'BUY', icon: 'coin', variant: 'yellow', cls: 'wide', onClick: () => doBuy() });
  const play = button({ label: 'PLAY SOUND', icon: 'speaker', variant: 'cream', cls: 'wide', onClick: () => playPreview() });
  const detail = el('div', { class: 'panel lk-detail' }, dCat, dName, dStatus, play, buy, equip);

  const spinHint = el('div', { class: 'lk-spin-hint' }, icon('rematch'), el('span', { text: 'Drag to spin' }));
  const stage = el('div', { class: 'lk-stage', on: {
    pointerdown: (e) => {
      stage.setPointerCapture(e.pointerId);
      lastX = e.clientX;
    },
    pointermove: (e) => {
      if (!stage.hasPointerCapture(e.pointerId)) return;
      deps.services.locker.spin(e.clientX - lastX);
      lastX = e.clientX;
    },
  } }, spinHint);
  let lastX = 0;

  const left = el('div', { class: 'panel lk-left' }, tabStrip.el, shopNote, grid);
  /** Portrait phones: a rendered portrait instead of the turntable (which would fill the width). */
  const flatPreview = el('div', { class: 'lk-preview' });
  let flat = false;
  const header = el('div', { class: 'lk-header' },
    el('div', { class: 'lk-title inked', text: 'LOCKER' }), bucksChip,
    closeButton(() => deps.nav.back(), 'Close the Locker'));
  const root = el('div', { class: 'screen locker' }, stage, flatPreview, header, left, detail);

  function isEquipped(e: Entry): boolean {
    const c = deps.profile().cosmetics;
    return e.cat === 'identityPref' ? c.identityPref === e.id : c[e.cat] === e.id;
  }

  function status(e: Entry): ItemStatus | null {
    return e.cat === 'identityPref' ? null : profiles.itemStatus(e.cat, e.id);
  }

  function previewIdentity(c: Cosmetics): number {
    return c.identityPref < IDENTITIES.length ? c.identityPref : 0;
  }

  function applyPreview(): void {
    if (!flat) {
      deps.services.locker.set(preview, previewIdentity(preview));
      return;
    }
    const pic = deps.services.portrait(preview, previewIdentity(preview), 256);
    if (pic) flatPreview.replaceChildren(pic);
    replayClass(flatPreview, 'swap');
  }

  function cardArt(e: Entry): { art: HTMLElement; lazy: boolean } {
    const colorblind = deps.settings().accessibility.colorblind;
    if (e.cat === 'identityPref') {
      return { art: e.id === NO_IDENTITY_PREF ? el('span', { class: 'lk-art-icon' }, icon('dice')) : el('span', { class: 'lk-art-id' }, identityBadge(e.id, colorblind, 'idbadge xl')), lazy: false };
    }
    if (e.cat === 'color') {
      const hex = e.item?.hex ?? '#FF5A4E';
      return { art: el('span', { class: 'lk-art-bean', style: { '--swatch': hex } }, beanIcon(hex, 'happy', 'ico lk-bean')), lazy: false };
    }
    if (PORTRAIT_CATS.has(e.cat)) return { art: el('span', { class: 'lk-art-portrait loading' }, beanIcon('#E5DCEB', 'happy', 'ico lk-bean ghostly')), lazy: true };
    const key = e.item?.key ?? '';
    const ic = e.cat === 'honk' ? 'honk' : e.cat === 'scream' ? 'scream' : e.cat === 'trail' ? (TRAIL_ICONS[key] ?? 'trail') : CELEBRATION_ICONS[key] ?? 'party';
    return { art: el('span', { class: 'lk-art-icon' }, icon(ic)), lazy: false };
  }

  function renderCard(c: Card, e: Entry): void {
    const st = status(e);
    const equipped = isEquipped(e);
    const locked = st !== null && !st.unlocked;
    toggleClass(c.node, 'equipped', equipped);
    toggleClass(c.node, 'locked', locked);
    // Today's shop items are for sale, not locked away: they show in full color with their price.
    toggleClass(c.node, 'for-sale', locked && st?.state === 'shop' && st.inShop);
    toggleClass(c.node, 'selected', selected !== null && selected.cat === e.cat && selected.id === e.id);
    c.node.setAttribute('aria-selected', String(selected !== null && selected.cat === e.cat && selected.id === e.id));
    const badge = c.node.querySelector('.lk-badge');
    if (badge) {
      badge.replaceChildren(...(equipped ? [icon('checkBadge')]
        : locked && st ? (st.state === 'shop' ? [icon('coin'), el('b', { text: String(st.price) })] : [icon('lock'), el('b', { text: `LV ${st.unlockLevel}` })])
          : st?.state === 'owned' ? [el('b', { text: 'OWNED' })] : []));
      toggleClass(badge as HTMLElement, 'shop', locked && st?.state === 'shop');
    }
  }

  function buildGrid(): void {
    const list = tab === 'shop' ? profiles.shop().map((s) => ({ cat: s.item.cat, id: s.item.id, name: s.item.name, item: s.item })) : entriesFor(tab);
    lazyQueue = [];
    cards = list.map((e, i) => {
      const { art, lazy } = cardArt(e);
      const node = el('button', { class: `lk-card${tab === 'shop' ? ' shop' : ''}`, attrs: { type: 'button', role: 'option', 'aria-label': e.name }, style: { 'animation-delay': `${Math.min(i, 16) * 22}ms` } },
        art, el('span', { class: 'lk-card-name', text: e.name }), el('span', { class: 'lk-badge' }),
        tab === 'shop' && e.item ? el('span', { class: 'lk-card-cat', text: TAB_LABELS[e.item.cat] }) : null);
      const card: Card = { node, cat: e.cat, id: e.id, art, lazy };
      node.addEventListener('click', () => select(e, true));
      node.addEventListener('focus', () => {
        if (deps.services.input.device !== 'touch') select(e, false);
      });
      renderCard(card, e);
      if (lazy) lazyQueue.push(card);
      return card;
    });
    grid.replaceChildren(...cards.map((c) => c.node));
    toggleClass(grid, 'shop-grid', tab === 'shop');
    setHidden(shopNote, tab !== 'shop');
    if (tab === 'shop') {
      const h = Math.floor(shopSecondsLeft() / 3600);
      const m = Math.floor((shopSecondsLeft() % 3600) / 60);
      shopNote.replaceChildren(icon('timer'), el('span', { text: `New stock in ${h}h ${m}m • Spend Bonk Bucks from matches and challenges` }));
    }
  }

  function entryOf(c: Card): Entry {
    const item = c.cat === 'identityPref' ? null : CATALOG.find((it) => it.cat === c.cat && it.id === c.id) ?? null;
    return { cat: c.cat, id: c.id, name: c.cat === 'identityPref' ? (c.id === NO_IDENTITY_PREF ? 'No preference' : IDENTITIES[c.id].name) : item?.name ?? (c.cat === 'hat' ? 'No hat' : ''), item };
  }

  function refreshCards(): void {
    for (const c of cards) renderCard(c, entryOf(c));
  }

  function selectTab(id: TabId): void {
    tab = id;
    confirmBuy = false;
    buildGrid();
    const p = deps.profile().cosmetics;
    const first = cards.find((c) => (c.cat === 'identityPref' ? p.identityPref === c.id : p[c.cat] === c.id)) ?? cards[0];
    if (first) select(entryOf(first), false);
  }

  function select(e: Entry, fromClick: boolean): void {
    const same = selected !== null && selected.cat === e.cat && selected.id === e.id;
    selected = e;
    if (!same) confirmBuy = false;
    // Preview on the turntable even when locked.
    preview = { ...deps.profile().cosmetics };
    if (e.cat === 'identityPref') preview.identityPref = e.id;
    else preview[e.cat] = e.id;
    applyPreview();
    if (!same && (e.cat === 'honk' || e.cat === 'scream') && fromClick) playPreview();
    refreshCards();
    renderDetail();
    if (fromClick && same) {
      const st = status(e);
      if (st === null || st.unlocked) doEquip();
    }
  }

  function playPreview(): void {
    const e = selected;
    if (!e || (e.cat !== 'honk' && e.cat !== 'scream')) return;
    deps.services.preview(e.cat, e.id);
    replayClass(play, 'pressed');
  }

  function renderDetail(): void {
    const e = selected;
    setHidden(detail, e === null);
    if (!e) return;
    setText(dName, e.name);
    setText(dCat, e.cat === 'identityPref' ? 'Identity color & glyph' : TAB_LABELS[e.cat]);
    const st = status(e);
    const equipped = isEquipped(e);
    const p = deps.profile();
    setHidden(play, e.cat !== 'honk' && e.cat !== 'scream');
    let text = '';
    let tone = '';
    if (e.cat === 'identityPref') text = e.id === NO_IDENTITY_PREF ? 'You get any free identity in a room.' : `Your preferred match color and glyph — honored when free.`;
    else if (!st) text = '';
    else if (st.state === 'starter') text = 'Starter item';
    else if (st.state === 'level') text = `Unlocked at Level ${st.unlockLevel}`;
    else if (st.state === 'owned') text = 'Bought from the shop';
    else if (st.state === 'locked') {
      text = `Unlocks at Bean Level ${st.unlockLevel} (you are ${p.level})`;
      tone = 'locked';
    } else if (st.state === 'shop') {
      text = st.inShop ? `In today's shop for ${st.price} Bonk Bucks` : `Shop item (${st.price} Bucks) — not in today's rotation`;
      tone = 'shop';
    }
    setText(dStatus, text);
    dStatus.dataset.tone = tone;
    const unlocked = st === null || st.unlocked;
    setHidden(equip, !unlocked);
    setLabel(equip, equipped ? 'EQUIPPED' : 'EQUIP');
    toggleClass(equip, 'on', equipped);
    equip.disabled = equipped;
    const canShop = st !== null && st.state === 'shop' && st.inShop;
    setHidden(buy, !canShop);
    if (canShop && st) {
      const afford = p.bucks >= st.price;
      buy.disabled = !afford;
      setLabel(buy, confirmBuy ? `CONFIRM ${st.price}?` : afford ? `BUY ${st.price}` : `NEED ${st.price - p.bucks} MORE`);
      toggleClass(buy, 'confirm', confirmBuy);
    }
  }

  function doEquip(): void {
    const e = selected;
    if (!e) return;
    const ok = e.cat === 'identityPref' ? profiles.setIdentityPref(e.id) : profiles.equip(e.cat, e.id);
    if (!ok) {
      deps.services.playUi('error');
      replayClass(detail, 'shake');
      return;
    }
    deps.services.playUi('coin');
    replayClass(equip, 'pressed');
    const card = cards.find((c) => c.cat === e.cat && c.id === e.id);
    if (card) replayClass(card.node, 'equip-pop');
  }

  function doBuy(): void {
    const e = selected;
    if (!e || e.cat === 'identityPref') return;
    if (!confirmBuy) {
      confirmBuy = true;
      renderDetail();
      return;
    }
    confirmBuy = false;
    const r = profiles.buy(e.cat, e.id);
    if (r === 'ok') {
      deps.services.playUi('coin');
      const rect = buy.getBoundingClientRect();
      const host = root.getBoundingClientRect();
      confetti(root, rect.left - host.left + rect.width / 2, rect.top - host.top, ['#FFD24A', '#FF4F9A', '#3DDC97', '#4CB8FF'], 46);
      profiles.equip(e.cat, e.id);
    } else {
      deps.services.playUi('error');
      replayClass(detail, 'shake');
    }
    renderDetail();
  }

  deps.bus.on('profile', (p) => {
    if (root.hidden) return;
    setText(bucks, String(p.bucks));
    refreshCards();
    renderDetail();
  });

  return {
    el: root,
    onShow() {
      flat = window.innerHeight > window.innerWidth;
      toggleClass(root, 'flat', flat);
      if (!flat) deps.services.locker.enter();
      preview = { ...deps.profile().cosmetics };
      applyPreview();
      setText(bucks, String(deps.profile().bucks));
      tabStrip.select(tab);
      selectTab(tab);
    },
    onHide() {
      if (!flat) deps.services.locker.exit();
      lazyQueue = [];
    },
    onNav(action: NavAction) {
      if (action === 'tabL' || action === 'tabR') {
        tabStrip.step(action === 'tabL' ? -1 : 1);
        return true;
      }
      return false;
    },
    update() {
      // One portrait per frame keeps the grid responsive on slow GPUs.
      const next = lazyQueue.shift();
      if (next && next.cat !== 'identityPref') {
        const c: Cosmetics = { ...deps.profile().cosmetics, [next.cat]: next.id };
        const pic = deps.services.portrait(c, previewIdentity(deps.profile().cosmetics), CARD_PORTRAIT);
        if (pic) {
          next.art.classList.remove('loading');
          next.art.replaceChildren(pic);
        }
      }
    },
  };
}
